import { useEffect, useRef, useState } from "react";

/**
 * Autosave-on-blur input controller with optional client-side validation.
 *
 * Contract:
 *   - draft mirrors `value` until the user edits, then becomes their input
 *   - on blur: trim → run validator (if given) → if valid, call onSave
 *   - on invalid blur: don't save, set `error` to the message, keep draft typed
 *   - error clears as soon as the user types again
 *   - external value changes (refetch after save) overwrite the draft ONLY
 *     when the input is not focused, so a concurrent peer save's refetch
 *     can't stomp work-in-progress.
 *
 * `error` is the message string (or undefined) so the component can render it
 * directly under the field (the per-field error-map contract).
 *
 * KNOWN LIMIT: `validate` runs only on NON-empty trimmed input, so the hook can
 * surface *malformed* messages but cannot enforce *required* (an empty field
 * saves through as null). Every field wired through here is length-capped /
 * malformed-only, so that's acceptable — do NOT wire a truly-required field
 * here expecting an empty-message; use a submit-gated `checkField` map instead.
 */
export interface UseEditableFieldOptions {
  /** Optional validator. Return an error message to refuse the save, or
   *  undefined to allow it. Runs only on non-empty trimmed input. */
  validate?: (trimmed: string) => string | undefined;
  /** Called when blur sees a valid trimmed value different from `value`.
   *  Receives null for empty input. */
  onSave: (next: string | null) => void;
}

export interface UseEditableFieldReturn {
  draft: string;
  setDraft: (v: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  /** The validation message, or undefined when valid. */
  error: string | undefined;
}

export function useEditableField(
  value: string | null | undefined,
  { validate, onSave }: UseEditableFieldOptions,
): UseEditableFieldReturn {
  const [draft, setDraftState] = useState<string>(value ?? "");
  const [error, setError] = useState<string | undefined>(undefined);
  const focusedRef = useRef(false);

  // Sync external value → draft, but only when not focused.
  useEffect(() => {
    if (focusedRef.current) return;
    setDraftState(value ?? "");
  }, [value]);

  const setDraft = (next: string) => {
    setDraftState(next);
    if (error) setError(undefined);
  };

  const onFocus = () => {
    focusedRef.current = true;
  };

  const onBlur = () => {
    focusedRef.current = false;
    const trimmed = draft.trim();
    if (validate && trimmed) {
      const message = validate(trimmed);
      if (message) {
        setError(message);
        return;
      }
    }
    setError(undefined);
    if ((trimmed || null) !== (value || null)) {
      onSave(trimmed || null);
    }
  };

  return { draft, setDraft, onFocus, onBlur, error };
}
